import { motion } from 'framer-motion'
import { useRef, useState } from 'react'
import { ArrowLeft, Send } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { generateTopicUrl } from '../lib/urlUtils'
import { publishEntry } from '../lib/nostr'
import { announceNewTopic } from '../services/botService'
import DOMPurify from 'dompurify'
import { EntryToolbar } from '../components/Entry/EntryToolbar'
import { useNostrTopics } from '../hooks/useNostrTopics'
import { Search } from 'lucide-react'

export function CreateTopicPage() {
    const { pubkey, sk } = useAuth()
    const navigate = useNavigate()
    const [title, setTitle] = useState('')
    const [firstEntry, setFirstEntry] = useState('')
    const [error, setError] = useState('')
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const { topics } = useNostrTopics()

    const existingTopic = title.trim().length >= 3
        ? topics.find(t => t.slug === title.trim().toLowerCase().replace(/\s+/g, '-'))
        : null

    const createTopicMutation = useMutation({
        mutationFn: async ({ topicTitle, content }: { topicTitle: string, content: string }) => {
            if (!pubkey) throw new Error('User not authenticated')

            // Convert to lowercase (Ekşi style)
            const lowercaseTitle = topicTitle.trim().toLowerCase()
            const sanitizedTitle = DOMPurify.sanitize(lowercaseTitle, { ALLOWED_TAGS: [] })
            const sanitizedContent = DOMPurify.sanitize(content.trim())

            // Publish entry
            await publishEntry(sanitizedContent, sanitizedTitle, sk)

            // Announce new topic via bot (non-blocking)
            announceNewTopic(sanitizedTitle).catch(err =>
                console.warn('Bot announcement failed:', err)
            )

            return { title: sanitizedTitle }
        },
        onSuccess: (data: { title: string }) => {
            // Navigate using full title as ID for correct parsing
            navigate(generateTopicUrl(data.title, data.title))
        },
        onError: (error: Error) => {
            setError(error.message || 'Başlık oluşturulurken bir hata oluştu')
        },
    })

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        setError('')

        if (title.trim().length < 3) {
            setError('Başlık en az 3 karakter olmalıdır')
            return
        }

        if (firstEntry.trim().length < 10) {
            setError('İlk entry en az 10 karakter olmalıdır')
            return
        }

        createTopicMutation.mutate({ topicTitle: title.trim(), content: firstEntry.trim() })
    }

    if (!pubkey) {
        return (
            <div className="text-center py-12">
                <h2 className="font-poppins font-semibold text-2xl mb-4">Giriş Gerekli</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Başlık oluşturmak için giriş yapmalısınız.
                </p>
                <Link to="/login" className="btn-primary">
                    Giriş Yap
                </Link>
            </div>
        )
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto space-y-6"
        >
            {/* Header */}
            <div className="flex items-center gap-4">
                <Link
                    to="/"
                    className="p-2 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                </Link>
                <div className="flex-1">
                    <h1 className="font-poppins font-bold text-3xl lg:text-4xl text-gray-900 dark:text-white">
                        Yeni Başlık Oluştur
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">
                        Yeni bir konu hakkında başlık başlatın
                    </p>
                </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label htmlFor="title" className="block font-poppins font-medium text-gray-900 dark:text-white">
                            Başlık
                        </label>
                        <input
                            id="title"
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value.toLowerCase())}
                            placeholder="başlık giriniz (ör: uzayda hayat)"
                            className={`w-full px-4 py-3 rounded-xl border-2 bg-white dark:bg-surface-900 focus:outline-none transition-all lowercase ${existingTopic
                                ? 'border-mavi-500 shadow-lg shadow-mavi-500/10'
                                : 'border-surface-300 dark:border-surface-600 focus:ring-2 focus:ring-mavi-500'
                                }`}
                            minLength={3}
                            maxLength={100}
                            required
                        />
                        {existingTopic && (
                            <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="flex items-center gap-2 text-xs text-mavi-600 dark:text-mavi-400 font-poppins font-medium bg-mavi-50 dark:bg-mavi-900/20 p-2 rounded-lg"
                            >
                                <Search size={14} />
                                <span>Bu başlık zaten mevcut. Yazacağınız entry bu başlığa eklenecek.</span>
                            </motion.div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label htmlFor="firstEntry" className="block font-poppins font-medium text-gray-900 dark:text-white">
                            İlk Entry
                        </label>

                        {/* Toolbar */}
                        <EntryToolbar
                            textareaRef={textareaRef}
                            value={firstEntry}
                            onChange={setFirstEntry}
                        />

                        <div className="relative">
                            <textarea
                                id="firstEntry"
                                ref={textareaRef}
                                value={firstEntry}
                                onChange={(e) => setFirstEntry(e.target.value)}
                                placeholder="Başlık altındaki ilk fikri sen bırak..."
                                className="w-full p-4 rounded-xl border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 focus:outline-none focus:ring-2 focus:ring-mavi-500 focus:border-transparent resize-none transition-all font-merriweather leading-relaxed"
                                rows={8}
                                minLength={3}
                                required
                            />
                            <div className="absolute bottom-3 right-3 text-[10px] text-gray-400 font-mono">
                                {firstEntry.length} karakter
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-kirmizi-50 dark:bg-kirmizi-900/20 border border-kirmizi-200 dark:border-kirmizi-800 rounded-lg p-4">
                        <p className="text-kirmizi-700 dark:text-kirmizi-400 text-sm">{error}</p>
                    </div>
                )}


                <div className="flex gap-4">
                    <Link to="/" className="btn-secondary flex-1">
                        İptal
                    </Link>
                    <button
                        type="submit"
                        disabled={title.trim().length < 3 || createTopicMutation.isPending}
                        className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {createTopicMutation.isPending ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <Send className="w-5 h-5" />
                        )}
                        {existingTopic ? 'Entry Gönder' : 'Başlık Başlat'}
                    </button>
                </div>
            </form>
        </motion.div>
    )
}


